import { useCallback, useContext, useMemo, useState, memo } from 'react';
import { InteractionStepTaskComponent } from '../../../InteractionStep';
import {
  InteractionStepType,
  CandidateMessageWithMetadata,
  DEFAULT_MODEL_RESPONSE_RANKING_TITLE,
  DEFAULT_MODEL_RESPONSE_RANKING_SUBTITLE,
  ModelResponseRankingStepResponse,
} from '@scale/shared/types/chat';
import _ from 'lodash';
import { Modal, Markdown, Typography, Icon } from '@scale/scaleui';
import { Transition } from '@headlessui/react';
import { Alert } from '../../../TempComponents';
import { RadioCard } from '../../../RadioCard';
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd';
import DraggableCandidateCard from './DraggableCandidateCard';
import DropCandidatesZone from './DropCandidatesZone';
import { isModelRankingConsistent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import UtilitiesWrapper from 'react-scale/components/scalers/tasks/common/UtilitiesWrapper';
import {
  useMostRecentResponseForStepType,
  useMostRecentStepParamsForStepType,
  useStepParams,
  useStepResponse,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { useStepSubmitHandler } from '../../../hooks/useStepSubmitHandler';
import { StepWidthWrapper, StepWidth } from '../../../step/StepWidthWrapper';
import { StepCard } from '../../../step/StepCard';
import { CommentButton } from '../../../feedback/CommentButton';
import { ResponseTemplatingButton } from '../../../step/ResponseTemplatingButton';
import { PinButton } from '../../../step/PinButton';
import { useChatThemeStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { ChatTaskContext } from '../../../contexts/ChatTaskContext';

const ModelResponseRankingTaskComponent: InteractionStepTaskComponent = ({
  responseIndex,
  readOnly,
  isPinnedView,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const theme = useChatThemeStore(state => state.theme);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [expandedCandidate, setExpandedCandidate] = useState<CandidateMessageWithMetadata | null>(
    null,
  );

  const stepParams = useStepParams<InteractionStepType.ModelResponseRanking>(responseIndex);
  const response = useStepResponse<ModelResponseRankingStepResponse>(responseIndex);
  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });
  const lastModelResponseSelectorStep = useMostRecentResponseForStepType(
    InteractionStepType.ModelResponseSelector,
    responseIndex,
  );

  const textCollectionPerResponseStepResponse = useMostRecentResponseForStepType(
    InteractionStepType.TextCollectionPerResponse,
    responseIndex,
  );
  const textCollectionPerResponseStepParams = useMostRecentStepParamsForStepType(
    InteractionStepType.TextCollectionPerResponse,
    responseIndex,
  );

  // Since this component has its own state, candidatesToRank / rankedCandidates may be out of sync
  // We should always render the content from the latest version of the candidates
  const latestCandidatesById: Record<string, CandidateMessageWithMetadata> = useMemo(() => {
    const candidates = lastModelResponseSelectorStep?.context.candidates ?? [];
    return _.keyBy(candidates, 'id');
  }, [lastModelResponseSelectorStep?.context.candidates]);

  const { initialCandidatesToRank, initialRankedCandidates, displayOrder } = useMemo<{
    initialCandidatesToRank: CandidateMessageWithMetadata[];
    initialRankedCandidates: CandidateMessageWithMetadata[][];
    displayOrder: number[];
    // @ts-expect-error TODO: fix line 59 type - couldn't find a way to fix it.
  }>(() => {
    const candidates = lastModelResponseSelectorStep?.context.candidates;
    const displayOrder = lastModelResponseSelectorStep?.context.displayOrder;

    if (!candidates || !candidates.length || !displayOrder || !displayOrder.length)
      return { initialCandidatesToRank: [], initialRankedCandidates: [], displayOrder: [] };

    const shuffledCandidates = displayOrder.map(index => candidates[index]);

    // TODO: This default value should be generated from config, either explicitly configured by
    // the operator or inferred from the ModelResponseSelector step being ranked
    // 1. If there is a step config, then create an array for each configured container (CLASSIFICATION TYPE)
    // 2. If not, create n containers where n is the number of candidates to classify (ORDERING TYPE)
    // Only ORDERING TYPE is supported as default
    const rankingResults: string[][] =
      response?.context.rankedCandidates.map(group => group.map(candidate => candidate.id)) ||
      shuffledCandidates?.map(() => []) ||
      [];

    // We have an array of IDs; should be replaced by an array of candidates
    const initialRankedCandidates =
      rankingResults.map(group => {
        return group
          .map((id: string) => shuffledCandidates?.find(candidate => candidate.id === id))
          .filter(e => e);
      }) || [];

    const rankedIds = _.flatten(rankingResults);
    const initialCandidatesToRank =
      shuffledCandidates?.filter(candidate => !rankedIds.includes(candidate.id)) || [];

    return { initialCandidatesToRank, initialRankedCandidates, displayOrder };
  }, [lastModelResponseSelectorStep, response?.context.rankedCandidates]);

  const [candidatesToRank, setCandidatesToRank] =
    useState<CandidateMessageWithMetadata[]>(initialCandidatesToRank);

  const [rankedCandidates, setRankedCandidates] =
    useState<CandidateMessageWithMetadata[][]>(initialRankedCandidates);

  const handleContinue = useCallback(
    (rankedList: CandidateMessageWithMetadata[][]) => {
      setErrorMessage('');

      const bestCandidate = _.flatten(rankedList)?.[0] || null;
      const rankedCandidatesIds = rankedList.map(group => group.map(candidate => candidate.id));

      const { isValid, errorMessage } = isModelRankingConsistent(
        textCollectionPerResponseStepResponse,
        textCollectionPerResponseStepParams,
        rankedCandidatesIds,
      );

      if (!isValid) {
        setErrorMessage(errorMessage);
        return;
      }

      let output = bestCandidate?.message.content || '';
      if (stepParams.params?.always_send_given_id) {
        const forcedCandidate = rankedList
          .flat()
          .find(c => c?.sourceId === stepParams.params?.always_send_given_id);
        if (forcedCandidate) {
          output = forcedCandidate.message.content;
        }
      }

      onSubmit(
        {
          type: InteractionStepType.ModelResponseRanking,
          context: {
            rankedCandidates: rankedList.map(group =>
              group.map(candidate => latestCandidatesById[candidate.id] ?? candidate),
            ),
          },
          output,
        },
        responseIndex,
        stepParams,
        undefined,
        undefined,
      );
    },
    [
      onSubmit,
      stepParams,
      responseIndex,
      textCollectionPerResponseStepParams,
      textCollectionPerResponseStepResponse,
      latestCandidatesById,
    ],
  );

  const handleReorder = useCallback(
    (result: DropResult) => {
      const { source, destination } = result;

      // Dropped outside droppable containers
      if (!destination) {
        return;
      }

      const rankedList = _.cloneDeep(rankedCandidates);
      const toRankList = _.cloneDeep(candidatesToRank);

      const sourceGroupIndex = parseInt(source.droppableId[0]);
      const sourceGroup = rankedList[sourceGroupIndex];

      const destinationGroupIndex = parseInt(destination.droppableId[0]);
      const destinationGroup = rankedList[destinationGroupIndex];

      // Same container - reorder
      if (source.droppableId === destination.droppableId) {
        const [item] = sourceGroup.splice(source.index, 1);
        sourceGroup.splice(destination.index, 0, item);
        rankedList[sourceGroupIndex] = sourceGroup;

        setRankedCandidates(rankedList);
      }

      // Dropping from "toRank" container - add to new group
      if (source.droppableId.includes('toRank')) {
        const [item] = toRankList.splice(source.index, 1);

        destinationGroup.push(item);
        rankedList[destinationGroupIndex] = destinationGroup;

        setRankedCandidates(rankedList);
        setCandidatesToRank(toRankList);
      } else if (source.droppableId !== destination.droppableId) {
        // Different container, not from toRank - move + reorder
        const [item] = sourceGroup.splice(source.index, 1);
        destinationGroup.splice(destination.index, 0, item);

        rankedList[destinationGroupIndex] = destinationGroup;
        rankedList[sourceGroupIndex] = sourceGroup;

        setRankedCandidates(rankedList);
      }

      if (rankedList.some(group => group.length > 1) && !stepParams.params.allow_ties) {
        setErrorMessage('Only one candidate per group allowed.');
        return;
      }

      if (
        rankedList.every(group => group.length === 1) ||
        (toRankList.length === 0 && stepParams.params.allow_ties)
      ) {
        handleContinue(rankedList);
        return;
      }

      setErrorMessage('');
    },
    [candidatesToRank, handleContinue, rankedCandidates, stepParams],
  );

  return (
    <StepWidthWrapper width={isPinnedView ? StepWidth.Full : StepWidth.Responsive}>
      <Transition
        show={true}
        enter="transition-all duration-300"
        enterFrom="opacity-0 -translate-y-4"
        enterTo="opacity-100 translate-y-0 "
        leave="transition-all duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-4"
      >
        <StepCard readOnly={readOnly}>
          <StepCard.Header>
            <StepCard.HeaderLeft>
              <StepCard.Icon icon="award" />
              <StepCard.HeaderColumn>
                <StepCard.Title>
                  {stepParams.title || DEFAULT_MODEL_RESPONSE_RANKING_TITLE}
                </StepCard.Title>
                <StepCard.Subtitle>
                  {stepParams.subtitle || DEFAULT_MODEL_RESPONSE_RANKING_SUBTITLE}
                </StepCard.Subtitle>
              </StepCard.HeaderColumn>
            </StepCard.HeaderLeft>
            <StepCard.HeaderRight>
              {!taskContext.enableUnifiedFeedback && (
                <CommentButton
                  visible={true}
                  childId={constructStepChildId(stepParams)}
                  turnIndex={stepParams.turn}
                />
              )}
              <ResponseTemplatingButton
                childId={constructStepChildId(stepParams)}
                metadata={{ stepIndex: responseIndex }}
              />
              <PinButton responseIndex={responseIndex} />
            </StepCard.HeaderRight>
          </StepCard.Header>
          <StepCard.Body>
            <RadioCard.Group value={''} onChange={() => null}>
              <DragDropContext onDragEnd={handleReorder}>
                <div
                  className={`w-full grid grid-cols-${
                    rankedCandidates.length > 3 ? 4 : rankedCandidates.length
                  } gap-4`}
                >
                  {rankedCandidates.map((group, index) => {
                    return (
                      <div className="w-full" key={`droppable_${index}`}>
                        <Droppable droppableId={`${index}-droppable`} isDropDisabled={readOnly}>
                          {provided => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                              <RadioCard
                                value={null}
                                className="h-full min-[800px]:flex-1 max-[800px]:w-96"
                                contentStyles={{
                                  ...(!group.length ? { height: '200px' } : {}),
                                  padding: '0.5rem',
                                }}
                                subtitle={
                                  <Typography.Subtitle3 color="text-neutral-800">
                                    {index === 0
                                      ? `1. Best answer`
                                      : index === rankedCandidates.length - 1
                                      ? `${index + 1}. Worst answer`
                                      : `${index + 1}.`}
                                  </Typography.Subtitle3>
                                }
                                disabled={true}
                              >
                                {group.length ? (
                                  <>
                                    {group.map(
                                      (candidate, displayIndex) =>
                                        latestCandidatesById[candidate.id] && (
                                          <DraggableCandidateCard
                                            key={`draggableComponent-${candidate.id}`}
                                            candidate={latestCandidatesById[candidate.id]}
                                            candidateDisplayIndex={displayOrder.indexOf(
                                              candidate.index,
                                            )}
                                            draggableIndex={displayIndex}
                                            onExpandClick={() => {
                                              setExpandedCandidate(candidate);
                                              setOpenModal(true);
                                            }}
                                            isDragDisabled={readOnly}
                                            enableRTLText={stepParams.params.enable_rtl_text}
                                          />
                                        ),
                                    )}
                                  </>
                                ) : (
                                  <DropCandidatesZone />
                                )}

                                {provided.placeholder}
                              </RadioCard>
                            </div>
                          )}
                        </Droppable>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4">
                  <Droppable
                    droppableId="toRank-droppable"
                    isDropDisabled={true}
                    direction="horizontal"
                  >
                    {provided => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={`grid grid-cols-${
                          rankedCandidates.length > 3 ? 4 : rankedCandidates.length
                        } gap-4`}
                      >
                        {candidatesToRank.map(
                          (candidate, displayIndex) =>
                            latestCandidatesById[candidate.id] && (
                              <DraggableCandidateCard
                                key={`draggableComponent-${candidate.id}`}
                                candidate={latestCandidatesById[candidate.id]}
                                candidateDisplayIndex={displayOrder.indexOf(candidate.index)}
                                draggableIndex={displayIndex}
                                enableRTLText={stepParams.params.enable_rtl_text}
                                onExpandClick={() => {
                                  setExpandedCandidate(candidate);
                                  setOpenModal(true);
                                }}
                              />
                            ),
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              </DragDropContext>
            </RadioCard.Group>
            <div className={'flex gap-2 justify-center'}>
              {errorMessage && (
                <Alert variant="danger">
                  <Typography.Paragraph2>{errorMessage}</Typography.Paragraph2>
                </Alert>
              )}
            </div>
          </StepCard.Body>
        </StepCard>
      </Transition>
      <Modal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setTimeout(() => setExpandedCandidate(null), 200);
        }}
        theme={theme}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        title={expandedCandidate && `Candidate ${expandedCandidate?.index + 1}`}
      >
        <div className="max-w-2xl max-h-96">
          <UtilitiesWrapper enableRTL={stepParams.params.enable_rtl_text}>
            <div className="relative">
              {!taskContext.disableResponseSelectorCopying && (
                <Icon
                  className="absolute top-0 right-0 text-neutral-500"
                  variant="neutral"
                  size="sm"
                  icon="copy"
                  tooltip="Copy this response"
                  tooltipProps={{
                    delayDuration: 0,
                    side: 'left',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(expandedCandidate?.message.content || '');
                  }}
                />
              )}
              <Markdown className="text-sm" markdown={expandedCandidate?.message.content || ''} />
            </div>
          </UtilitiesWrapper>
        </div>
      </Modal>
    </StepWidthWrapper>
  );
};

export default memo(ModelResponseRankingTaskComponent);
